import { useState } from "react";
import type { AIVisibility, AIVisibilitySuggestion, SeverityItem } from "@/types/analysis";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Info,
  Brain,
  ChevronDown,
  TrendingUp,
  XCircle,
  ArrowRight,
} from "lucide-react";

const descriptions: Record<string, string> = {
  "Retrieval Friendliness":
    "How easily AI tools like ChatGPT and Perplexity can extract useful answers from your content.",
  "Knowledge Structuring":
    "How well your content is organized with clear headings, entities, and schema markup for AI understanding.",
  "Citation Likelihood":
    "How likely AI systems are to cite your page as a source based on authority and factual content.",
};

function getScoreQuality(score: number) {
  if (score >= 80) return { label: "Excellent", className: "bg-success/10 text-success border-success/30" };
  if (score >= 60) return { label: "Good", className: "bg-primary/10 text-primary border-primary/30" };
  if (score >= 40) return { label: "Needs Work", className: "bg-warning/10 text-warning border-warning/30" };
  return { label: "Poor", className: "bg-destructive/10 text-destructive border-destructive/30" };
}

function getReadinessTier(mentionMax: number) {
  if (mentionMax <= 10) return { label: "Very Low", className: "bg-destructive/10 text-destructive border-destructive/30" };
  if (mentionMax <= 20) return { label: "Low", className: "bg-warning/10 text-warning border-warning/30" };
  if (mentionMax <= 35) return { label: "Moderate", className: "bg-primary/10 text-primary border-primary/30" };
  if (mentionMax <= 50) return { label: "High", className: "bg-success/10 text-success border-success/30" };
  return { label: "Very High", className: "bg-success/10 text-success border-success/30" };
}

function estimateMentions(data: AIVisibility, backlinkScore?: number) {
  const weightedScore =
    data.retrievalFriendliness.score * 0.35 +
    data.knowledgeStructuring.score * 0.25 +
    data.citationLikelihood.score * 0.4;
  const authorityMultiplier = backlinkScore != null ? 0.5 + (backlinkScore / 100) * 1.0 : 1;
  const baseEstimate = weightedScore * authorityMultiplier * 0.5;
  const mentionMin = Math.floor(baseEstimate * 0.7);
  const mentionMax = Math.ceil(baseEstimate * 1.3);
  return { mentionMin, mentionMax };
}

const impactConfig = {
  high: { label: "High Impact", boost: "+3-5 mentions/mo", className: "bg-success/10 text-success border-success/30" },
  medium: { label: "Medium Impact", boost: "+1-2 mentions/mo", className: "bg-warning/10 text-warning border-warning/30" },
  low: { label: "Low Impact", boost: "<1 mention/mo", className: "bg-muted text-muted-foreground border-border" },
};

function isSuggestionObject(s: string | AIVisibilitySuggestion): s is AIVisibilitySuggestion {
  return typeof s === "object" && "text" in s;
}

const statusIcon = (status: SeverityItem["status"]) => {
  if (status === "pass") return <CheckCircle2 className="w-4 h-4 text-success" />;
  if (status === "warning") return <AlertTriangle className="w-4 h-4 text-warning" />;
  return <XCircle className="w-4 h-4 text-destructive" />;
};

const severityColor = (s: SeverityItem["severity"]) => {
  const map = {
    low: "bg-info/20 text-info",
    medium: "bg-warning/20 text-warning",
    high: "bg-destructive/20 text-destructive",
    critical: "bg-destructive text-destructive-foreground",
  };
  return map[s];
};

const ChecklistSection = ({
  title,
  description,
  items,
  score,
  confidence,
  onViewActionPlan,
}: {
  title: string;
  description: string;
  items?: SeverityItem[];
  score: number;
  confidence: number;
  onViewActionPlan?: () => void;
}) => {
  const [open, setOpen] = useState(true);
  const issueCount = items?.filter((i) => i.status === "warning" || i.status === "fail").length ?? 0;
  const quality = getScoreQuality(score);

  return (
    <Card className="border-border bg-card">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base">{title}</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">{description}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={quality.className}>{quality.label}</Badge>
                <Badge variant="outline" className="font-mono">{score}/100</Badge>
                <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-2">
            {issueCount > 0 && onViewActionPlan && (
              <button
                onClick={onViewActionPlan}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-primary/5 border border-primary/10 text-xs text-primary hover:bg-primary/10 transition-colors"
              >
                <span className="font-medium">
                  {issueCount} improvement{issueCount > 1 ? "s" : ""} added to Action Plan
                </span>
                <span className="flex items-center gap-1">
                  View fixes <ArrowRight className="w-3 h-3" />
                </span>
              </button>
            )}
            {items?.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/20">
                {statusIcon(item.status)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground">{item.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${severityColor(item.severity)}`}>
                      {item.severity}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{item.explanation}</p>
                  {item.evidence && (
                    <p className="text-xs text-muted-foreground/70 mt-1 font-mono bg-background/50 p-1.5 rounded break-words">
                      {item.evidence}
                    </p>
                  )}
                  {(item.status === "warning" || item.status === "fail") && item.recommendation && item.recommendation !== "N/A" && (
                    <div className="mt-2 space-y-1.5">
                      <p className="text-xs text-foreground/80">
                        <span className="font-medium">Recommendation:</span> {item.recommendation}
                      </p>
                      {onViewActionPlan && (
                        <button
                          onClick={onViewActionPlan}
                          className="inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 transition-colors font-medium"
                        >
                          <ArrowRight className="w-3 h-3" />
                          Added to Action Plan – View fixes
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {(!items || items.length === 0) && (
              <p className="text-xs text-muted-foreground italic px-3 py-2">No checklist items returned for this section.</p>
            )}
            <div className="pt-1">
              <Badge variant="outline" className="text-[10px]">Confidence: {confidence}%</Badge>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

const SuggestionItem = ({ suggestion }: { suggestion: string | AIVisibilitySuggestion }) => {
  const [open, setOpen] = useState(false);

  if (!isSuggestionObject(suggestion)) {
    return (
      <div className="text-sm text-foreground flex items-start gap-2 p-3 rounded bg-primary/5">
        <Lightbulb className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
        {suggestion}
      </div>
    );
  }

  const config = impactConfig[suggestion.impact];

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded border border-border bg-primary/5 overflow-hidden">
        <CollapsibleTrigger className="w-full text-left p-3 flex items-start gap-2 hover:bg-primary/10 transition-colors cursor-pointer">
          <Lightbulb className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
          <div className="flex-1 space-y-1.5">
            <p className="text-sm text-foreground">{suggestion.text}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={config.className}>{config.label}</Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                {config.boost}
              </span>
            </div>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground mt-0.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 pt-0 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground mt-2 mb-1">Example:</p>
            <div className="rounded bg-muted/60 p-2.5 text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed">
              {suggestion.example}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export const AIVisibilityTab = ({
  data,
  backlinkScore,
  onViewActionPlan,
}: {
  data: AIVisibility;
  backlinkScore?: number;
  onViewActionPlan?: () => void;
}) => {
  const { mentionMin, mentionMax } = estimateMentions(data, backlinkScore);
  const tier = getReadinessTier(mentionMax);

  return (
    <div className="space-y-4 mt-4">
      {/* AI Visibility Readiness Card */}
      <Card className="border-primary/30 bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            Estimated AI Mention Potential
            <Info className="w-4 h-4 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-4xl font-bold text-foreground">
              {mentionMin}–{mentionMax}
            </span>
            <span className="text-sm text-muted-foreground">mentions / month</span>
            <Badge variant="outline" className={tier.className}>{tier.label}</Badge>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            This reflects how AI-visibility friendly your page structure and content are — not guaranteed citation counts.
          </p>
          <div className="rounded-md bg-muted/50 p-3 flex items-start gap-2">
            <Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              This is a projection based on your page's content structure, AI-readiness signals, and authority indicators. Actual AI mentions depend on many external factors including search query volume, competition, AI model training data, and platform-specific algorithms. Use this as a guide to improve your content's AI-friendliness.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Checklist sections */}
      <ChecklistSection
        title="Retrieval Friendliness"
        description={descriptions["Retrieval Friendliness"]}
        items={data.retrievalFriendliness.items}
        score={data.retrievalFriendliness.score}
        confidence={data.retrievalFriendliness.confidence}
        onViewActionPlan={onViewActionPlan}
      />
      <ChecklistSection
        title="Knowledge Structuring"
        description={descriptions["Knowledge Structuring"]}
        items={data.knowledgeStructuring.items}
        score={data.knowledgeStructuring.score}
        confidence={data.knowledgeStructuring.confidence}
        onViewActionPlan={onViewActionPlan}
      />
      <ChecklistSection
        title="Citation Likelihood"
        description={descriptions["Citation Likelihood"]}
        items={data.citationLikelihood.items}
        score={data.citationLikelihood.score}
        confidence={data.citationLikelihood.confidence}
        onViewActionPlan={onViewActionPlan}
      />

      {/* Narrative supplement: strengths / weaknesses */}
      {(data.strengths?.length > 0 || data.weaknesses?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.strengths?.length > 0 && (
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-success" /> Strengths
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.strengths.map((s, i) => (
                  <div key={i} className="text-sm text-foreground flex items-start gap-2 p-2 rounded bg-success/5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-success mt-0.5 shrink-0" />
                    {s}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {data.weaknesses?.length > 0 && (
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning" /> Weaknesses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.weaknesses.map((w, i) => (
                  <div key={i} className="text-sm text-foreground flex items-start gap-2 p-2 rounded bg-warning/5">
                    <AlertTriangle className="w-3.5 h-3.5 text-warning mt-0.5 shrink-0" />
                    {w}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {data.suggestions?.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-primary" /> Suggestions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.suggestions.map((s, i) => (
              <SuggestionItem key={i} suggestion={s} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
